import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup, EditableSlotGroup } from '@vibesites/runtime/react';
import { RichTextContent } from './shared/RichTextContent';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

/**
 * C26 Hub Features — "What's Included" grid describing the four benefits
 * residents receive through The Residences at Bluhawk's official partnership
 * with C26 Hub Gym and Pool: structured gym access, pool access, two group
 * fitness classes per week, and reservation-based scheduling through the
 * Momence and UniFi apps.
 */

// ─── Slot group definitions (one literal per feature — static extraction) ──

const feature1Def: SlotGroupDefinition = {
  label: 'Benefit 1 Image',
  description: 'Gym access — Monday through Sunday, 5:00 AM to 2:00 PM.',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?auto=format&fit=crop&w=1200&q=70',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Strength training floor with free weights and functional equipment at C26 Hub',
    },
  },
};

const feature2Def: SlotGroupDefinition = {
  label: 'Benefit 2 Image',
  description: 'Pool access during regular C26 operating hours.',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        'https://images.unsplash.com/photo-1530549387789-4c1017266635?auto=format&fit=crop&w=1200&q=70',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Indoor 25-yard lap pool with blue lane lines at C26 Hub',
    },
  },
};

const feature3Def: SlotGroupDefinition = {
  label: 'Benefit 3 Image',
  description: 'Two group fitness classes per week included.',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        'https://images.unsplash.com/photo-1518611012118-696072aa579a?auto=format&fit=crop&w=1200&q=70',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Group fitness class with members doing functional training at C26 Hub',
    },
  },
};

const feature4Def: SlotGroupDefinition = {
  label: 'Benefit 4 Image',
  description: 'Reservation-based scheduling through the Momence and UniFi apps.',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?auto=format&fit=crop&w=1200&q=70',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Smartphone showing a class reservation calendar',
    },
  },
};

// ─── Icon badges (inline SVG, no external deps) ───────────────────────────

type IconKey = 'gym' | 'pool' | 'classes' | 'reserve';

function FeatureIcon({ icon }: { icon: IconKey }) {
  switch (icon) {
    case 'gym':
      return (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M6 14l-2 2 2 2" />
          <path d="M18 14l2 2-2 2" />
          <rect x="7" y="10" width="10" height="4" rx="1" />
          <path d="M2 16h2" />
          <path d="M20 16h2" />
        </svg>
      );
    case 'pool':
      return (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M2 18c2 0 2-1 4-1s2 1 4 1 2-1 4-1 2 1 4 1 2-1 4-1" />
          <path d="M2 22c2 0 2-1 4-1s2 1 4 1 2-1 4-1 2 1 4 1 2-1 4-1" />
          <path d="M7 14V5a2 2 0 0 1 2-2" />
          <path d="M17 14V5a2 2 0 0 0-2-2" />
          <path d="M7 9h10" />
        </svg>
      );
    case 'classes':
      return (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="12" cy="5" r="2" />
          <path d="M9 14l3-4 3 4" />
          <path d="M12 10v10" />
          <path d="M7 20l5-6 5 6" />
        </svg>
      );
    case 'reserve':
      return (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="5" y="4" width="14" height="18" rx="2" />
          <path d="M9 8h6" />
          <path d="M9 12h6" />
          <path d="M9 16h4" />
        </svg>
      );
  }
}

// ─── Component ────────────────────────────────────────────────────────────

export function C26HubFeatures() {
  const img1 = useSlotGroup('c26hubfeatures.feature1_image', feature1Def);
  const img2 = useSlotGroup('c26hubfeatures.feature2_image', feature2Def);
  const img3 = useSlotGroup('c26hubfeatures.feature3_image', feature3Def);
  const img4 = useSlotGroup('c26hubfeatures.feature4_image', feature4Def);

  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  return (
    <section
      ref={sectionRef}
      className="reveal-fade-up c26-features"
    >
      <div className="c26-features__container">

        {/* ── Section header ── */}
        <div className="c26-features__header">
          <RichTextContent
            path="c26hubfeatures.heading"
            label="Heading"
            defaultValue="<p>What&rsquo;s Included</p>"
            as="h2"
            className="c26-features__heading"
          />
        </div>

        {/* ── Benefit grid ── */}
        <div className="c26-features__grid">

          {/* Benefit 1 — Gym access */}
          <article className="c26-feature">
            <EditableSlotGroup
              groupPath="c26hubfeatures.feature1_image"
              definition={feature1Def}
              className="c26-feature__media"
            >
              <VibesitesImage
                url={img1.image || ''}
                alt={img1.alt || ''}
                className="c26-feature__img"
                sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
              />
            </EditableSlotGroup>
            <div className="c26-feature__body">
              <span className="c26-feature__badge" aria-hidden="true">
                <FeatureIcon icon="gym" />
              </span>
              <RichTextContent
                path="c26hubfeatures.feature1_title"
                label="Benefit 1 Title"
                defaultValue="<p>Gym Access</p>"
                as="h3"
                className="c26-feature__title"
              />
              <RichTextContent
                path="c26hubfeatures.feature1_body"
                label="Benefit 1 Description"
                defaultValue="<p><strong>Monday &ndash; Sunday, 5:00 AM &ndash; 2:00 PM.</strong> Reservation-based access to the full strength floor and training equipment.</p>"
                as="div"
                className="c26-feature__text"
              />
            </div>
          </article>

          {/* Benefit 2 — Pool access */}
          <article className="c26-feature">
            <EditableSlotGroup
              groupPath="c26hubfeatures.feature2_image"
              definition={feature2Def}
              className="c26-feature__media"
            >
              <VibesitesImage
                url={img2.image || ''}
                alt={img2.alt || ''}
                className="c26-feature__img"
                sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
              />
            </EditableSlotGroup>
            <div className="c26-feature__body">
              <span className="c26-feature__badge" aria-hidden="true">
                <FeatureIcon icon="pool" />
              </span>
              <RichTextContent
                path="c26hubfeatures.feature2_title"
                label="Benefit 2 Title"
                defaultValue="<p>Pool Access</p>"
                as="h3"
                className="c26-feature__title"
              />
              <RichTextContent
                path="c26hubfeatures.feature2_body"
                label="Benefit 2 Description"
                defaultValue="<p>Reservation-based lap-pool access during <strong>regular C26 operating hours</strong>, including dedicated swim lanes for adult residents.</p>"
                as="div"
                className="c26-feature__text"
              />
            </div>
          </article>

          {/* Benefit 3 — Group fitness classes */}
          <article className="c26-feature">
            <EditableSlotGroup
              groupPath="c26hubfeatures.feature3_image"
              definition={feature3Def}
              className="c26-feature__media"
            >
              <VibesitesImage
                url={img3.image || ''}
                alt={img3.alt || ''}
                className="c26-feature__img"
                sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
              />
            </EditableSlotGroup>
            <div className="c26-feature__body">
              <span className="c26-feature__badge" aria-hidden="true">
                <FeatureIcon icon="classes" />
              </span>
              <RichTextContent
                path="c26hubfeatures.feature3_title"
                label="Benefit 3 Title"
                defaultValue="<p>Two Classes Per Week</p>"
                as="h3"
                className="c26-feature__title"
              />
              <RichTextContent
                path="c26hubfeatures.feature3_body"
                label="Benefit 3 Description"
                defaultValue="<p>Reserve <strong>two (2) group fitness classes per week</strong> &mdash; functional training, HIIT, HYROX prep, cycling, and more.</p>"
                as="div"
                className="c26-feature__text"
              />
            </div>
          </article>

          {/* Benefit 4 — Reservations */}
          <article className="c26-feature">
            <EditableSlotGroup
              groupPath="c26hubfeatures.feature4_image"
              definition={feature4Def}
              className="c26-feature__media"
            >
              <VibesitesImage
                url={img4.image || ''}
                alt={img4.alt || ''}
                className="c26-feature__img"
                sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
              />
            </EditableSlotGroup>
            <div className="c26-feature__body">
              <span className="c26-feature__badge" aria-hidden="true">
                <FeatureIcon icon="reserve" />
              </span>
              <RichTextContent
                path="c26hubfeatures.feature4_title"
                label="Benefit 4 Title"
                defaultValue="<p>App-Based Reservations</p>"
                as="h3"
                className="c26-feature__title"
              />
              <RichTextContent
                path="c26hubfeatures.feature4_body"
                label="Benefit 4 Description"
                defaultValue="<p>All access managed exclusively through the <strong>Momence</strong> and <strong>UniFi</strong> apps &mdash; book workouts, classes, and pool times from your phone.</p>"
                as="div"
                className="c26-feature__text"
              />
            </div>
          </article>

        </div>
      </div>

      <style>{`
        .c26-features {
          background: var(--vibe-color-background, #ffffff);
          padding: 3.5rem 0 4rem;
        }
        @media (min-width: 768px) {
          .c26-features { padding: 5rem 0 5.5rem; }
        }

        .c26-features__container {
          max-width: 76rem;
          margin: 0 auto;
          padding: 0 1rem;
        }
        @media (min-width: 640px) { .c26-features__container { padding: 0 1.5rem; } }
        @media (min-width: 1024px) { .c26-features__container { padding: 0 2rem; } }

        .c26-features__header {
          text-align: center;
          max-width: 46rem;
          margin: 0 auto 2.5rem;
        }
        @media (min-width: 768px) {
          .c26-features__header { margin-bottom: 3.5rem; }
        }

        .c26-features__heading {
          font-family: var(--vibe-font-heading);
          font-size: clamp(2rem, 4vw, 2.75rem);
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.03em;
          line-height: 1.1;
          color: var(--vibe-color-text);
          margin: 0 0 1.25rem;
        }

        .c26-features__grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 1.5rem;
        }
        @media (min-width: 640px) {
          .c26-features__grid { grid-template-columns: repeat(2, 1fr); }
        }
        @media (min-width: 1024px) {
          .c26-features__grid { grid-template-columns: repeat(4, 1fr); gap: 1.5rem; }
        }

        .c26-feature {
          display: flex;
          flex-direction: column;
          background: #ffffff;
          border: 1px solid #e8e8ea;
          border-radius: var(--vibe-radius-lg, 12px);
          overflow: hidden;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
          transition: box-shadow 0.25s ease, border-color 0.25s ease, transform 0.25s ease;
        }
        .c26-feature:hover {
          box-shadow: 0 10px 28px rgba(0, 0, 0, 0.08);
          border-color: #dddde1;
          transform: translateY(-2px);
        }

        .c26-feature__media {
          position: relative;
          aspect-ratio: 4 / 3;
          background: #e8e8ea;
          overflow: hidden;
          display: block;
        }
        .c26-feature__img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
          transition: transform 0.5s ease;
        }
        .c26-feature:hover .c26-feature__img {
          transform: scale(1.04);
        }

        .c26-feature__body {
          position: relative;
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
          padding: 2.25rem 1.5rem 1.5rem;
          flex: 1;
        }

        .c26-feature__badge {
          position: absolute;
          top: -1.25rem;
          left: 1.25rem;
          width: 2.5rem;
          height: 2.5rem;
          border-radius: 50%;
          background: var(--vibe-color-secondary);
          color: var(--vibe-color-text-on-secondary, #ffffff);
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 6px 18px rgba(0, 0, 0, 0.18);
        }

        .c26-feature__title {
          font-family: var(--vibe-font-heading);
          font-size: 1.125rem;
          font-weight: 700;
          line-height: 1.3;
          text-transform: uppercase;
          letter-spacing: 0.03em;
          color: var(--vibe-color-text);
          margin: 0;
        }

        .c26-feature__text {
          font-family: var(--vibe-font-body);
          font-size: 0.95rem;
          line-height: 1.6;
          color: var(--vibe-color-text-muted);
        }
        .c26-feature__text strong {
          color: var(--vibe-color-text);
          font-weight: 600;
        }
      `}</style>
    </section>
  );
}

// ─── Content Schema ───────────────────────────────────────────────────────

export const contentSchema: ComponentContentSchema = {
  slot: 'C26HubFeatures',
  displayName: 'C26 Hub — What’s Included',
  tokens: {
    'c26hubfeatures.heading': {
      label: 'Section Heading',
      type: 'richtext',
      defaultValue: '<p>What&rsquo;s Included</p>',
    },
    'c26hubfeatures.feature1_title': {
      label: 'Benefit 1 Title',
      type: 'richtext',
      defaultValue: '<p>Gym Access</p>',
    },
    'c26hubfeatures.feature1_body': {
      label: 'Benefit 1 Description',
      type: 'richtext',
      defaultValue:
        '<p><strong>Monday &ndash; Sunday, 5:00 AM &ndash; 2:00 PM.</strong> Reservation-based access to the full strength floor and training equipment.</p>',
    },
    'c26hubfeatures.feature2_title': {
      label: 'Benefit 2 Title',
      type: 'richtext',
      defaultValue: '<p>Pool Access</p>',
    },
    'c26hubfeatures.feature2_body': {
      label: 'Benefit 2 Description',
      type: 'richtext',
      defaultValue:
        '<p>Reservation-based lap-pool access during <strong>regular C26 operating hours</strong>, including dedicated swim lanes for adult residents.</p>',
    },
    'c26hubfeatures.feature3_title': {
      label: 'Benefit 3 Title',
      type: 'richtext',
      defaultValue: '<p>Two Classes Per Week</p>',
    },
    'c26hubfeatures.feature3_body': {
      label: 'Benefit 3 Description',
      type: 'richtext',
      defaultValue:
        '<p>Reserve <strong>two (2) group fitness classes per week</strong> &mdash; functional training, HIIT, HYROX prep, cycling, and more.</p>',
    },
    'c26hubfeatures.feature4_title': {
      label: 'Benefit 4 Title',
      type: 'richtext',
      defaultValue: '<p>App-Based Reservations</p>',
    },
    'c26hubfeatures.feature4_body': {
      label: 'Benefit 4 Description',
      type: 'richtext',
      defaultValue:
        '<p>All access managed exclusively through the <strong>Momence</strong> and <strong>UniFi</strong> apps &mdash; book workouts, classes, and pool times from your phone.</p>',
    },
  },
  slotGroups: {
    'c26hubfeatures.feature1_image': feature1Def,
    'c26hubfeatures.feature2_image': feature2Def,
    'c26hubfeatures.feature3_image': feature3Def,
    'c26hubfeatures.feature4_image': feature4Def,
  },
};

(C26HubFeatures as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default C26HubFeatures;
